import React, { useState, useEffect } from 'react';
import { usePrayer } from '../../contexts/PrayerContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { calculationMethods } from '../../data/prayerData';
import { getVerseById } from '../../data/quranVerses';
import { User, MapPin, Calculator, LogOut, LogIn, ChevronRight, Moon, Sun, Clock, CalendarDays, Globe, BookOpen, Heart, Type, Plane } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import SEO from '../common/SEO';
import { CrescentStarIcon } from '../common/IslamicIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const LIKED_KEY = 'liked_verses_local';
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const weekStartOptions = [
  { id: 0, key: 'weekDays.sunday' },
  { id: 1, key: 'weekDays.monday' },
  { id: 6, key: 'weekDays.saturday' },
];

const LANGUAGE_OPTIONS = [
  { code: 'en', native: 'English' },
  { code: 'ar', native: 'العربية' },
  { code: 'fr', native: 'Français' },
  { code: 'es', native: 'Español' },
  { code: 'ur', native: 'اردو' },
];

const SettingsPage = () => {
  const { location, calculationMethod, updateCalculationMethod, refreshLocation } = usePrayer();
  const { user, isAuthenticated, login, logout, loading } = useAuth();
  const { isDarkMode, toggleTheme, language, setLanguage, isRTL, use24Hour, toggleTimeFormat, weekStartDay, updateWeekStartDay, fontSize, changeFontSize, travelMode, toggleTravelMode } = useTheme();
  const { t } = useTranslation();
  const [likedIds, setLikedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LIKED_KEY) || '[]'); }
    catch { return []; }
  });

  // Sync liked IDs from backend
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/verses/liked`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.liked_ids) setLikedIds(d.liked_ids); })
      .catch(() => {});
  }, [isAuthenticated]);

  const handleUnlike = async (verseId) => {
    const newIds = likedIds.filter(id => id !== verseId);
    setLikedIds(newIds);
    localStorage.setItem(LIKED_KEY, JSON.stringify(newIds));
    if (!isAuthenticated) return;
    await fetch(`${API_URL}/api/verses/like`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ verse_id: verseId, liked: false }),
    }).catch(() => {});
  };

  return (
    <>
      <SEO
        title={t('nav.settings')}
        description="App settings - Location, calculation method, theme and language"
        path="/settings"
      />
      <div
        className={`min-h-screen ${
          isDarkMode
            ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
            : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white'
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-4">
          <h1 className={`text-2xl font-bold text-center ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
            {t('nav.settings')}
          </h1>
        </div>

        <div className="px-4 space-y-4">
          {/* Profile Card */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.account')}
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-[var(--p-hex)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : isAuthenticated && user ? (
              <div className="space-y-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : (
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                      isDarkMode ? 'bg-white/10' : 'bg-[var(--p-hex)]/10'
                    }`}>
                      <User className={`w-7 h-7 ${isDarkMode ? 'text-gray-400' : 'text-[var(--p-hex)]'}`} />
                    </div>
                  )}
                  <div className={isRTL ? 'text-right' : ''}>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                  </div>
                </div>
                <Button
                  onClick={logout}
                  variant="outline"
                  data-testid="sign-out-btn"
                  className={`w-full justify-between ${
                    isDarkMode
                      ? 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'
                      : 'border-red-200 text-red-600 hover:bg-red-50'
                  }`}
                >
                  <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <LogOut className="w-4 h-4" />
                    {t('settings.signOut')}
                  </span>
                  <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('settings.signInSync')}
                </p>
                <Button
                  onClick={login}
                  data-testid="sign-in-btn"
                  className={`w-full bg-[var(--p-hex)] hover:bg-[var(--p-light-hex)] text-white justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <LogIn className="w-4 h-4" />
                    {t('settings.signInGoogle')}
                  </span>
                  <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            )}
          </div>

          {/* Location Card */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.location')}
            </h2>
            <button
              onClick={refreshLocation}
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
              } ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                }`}>
                  <MapPin className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {location?.city || 'Unknown'}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    {location?.country || t('settings.tapToUpdate')}
                  </p>
                </div>
              </div>
              <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Calculation Method */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.calculationMethod')}
            </h2>
            <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
              }`}>
                <Calculator className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('settings.chooseMethod')}
              </p>
            </div>
            <Select
              value={calculationMethod.toString()}
              onValueChange={(val) => updateCalculationMethod(parseInt(val))}
            >
              <SelectTrigger className={`w-full ${
                isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'
              }`}>
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-[#1e293b] border-white/10' : ''}>
                {calculationMethods.map((method) => (
                  <SelectItem
                    key={method.id}
                    value={method.id.toString()}
                    className={isDarkMode ? 'text-gray-200 focus:bg-white/10' : ''}
                  >
                    {method.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language Card */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.language')}
            </h2>
            <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-sky-500/20' : 'bg-sky-100'
              }`}>
                <Globe className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('settings.languageDesc')}
              </p>
            </div>
            <Select
              value={language}
              onValueChange={(val) => setLanguage(val)}
            >
              <SelectTrigger
                data-testid="language-select"
                className={`w-full ${
                  isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-[#1e293b] border-white/10' : ''}>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <SelectItem
                    key={lang.code}
                    value={lang.code}
                    data-testid={`lang-select-${lang.code}`}
                    className={isDarkMode ? 'text-gray-200 focus:bg-white/10' : ''}
                  >
                    {t(`languages.${lang.code}`)} — {lang.native}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Theme Card */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.theme')}
            </h2>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
              } ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-indigo-500/20' : 'bg-amber-100'
                }`}>
                  {isDarkMode
                    ? <Moon className="w-5 h-5 text-indigo-400" />
                    : <Sun className="w-5 h-5 text-amber-600" />}
                </div>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {isDarkMode ? t('settings.nightMode') : t('settings.dayMode')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    {t('settings.manualTheme')}
                  </p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isDarkMode ? 'bg-indigo-500' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            </button>
          </div>

          {/* Font Size Card */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.fontSize')}
            </h2>
            <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-sky-500/20' : 'bg-sky-100'
              }`}>
                <Type className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('settings.fontSizeDesc')}
              </p>
            </div>
            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {[
                { key: 'small', label: t('settings.fontSizeSmall'), preview: 'A' },
                { key: 'medium', label: t('settings.fontSizeMedium'), preview: 'A' },
                { key: 'large', label: t('settings.fontSizeLarge'), preview: 'A' },
              ].map(({ key, label, preview }) => (
                <button
                  key={key}
                  data-testid={`font-size-${key}`}
                  onClick={() => changeFontSize(key)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-colors ${
                    fontSize === key
                      ? 'border-[var(--p-hex)] bg-[var(--p-hex)]/10'
                      : isDarkMode
                        ? 'border-white/10 bg-white/5 hover:bg-white/10'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <span
                    className={`font-bold ${isDarkMode ? fontSize === key ? 'text-white' : 'text-gray-400' : fontSize === key ? 'text-[var(--p-hex)]' : 'text-gray-500'}`}
                    style={{ fontSize: key === 'small' ? '14px' : key === 'medium' ? '18px' : '22px' }}
                  >
                    {preview}
                  </span>
                  <span className={`text-[10px] font-medium ${
                    fontSize === key ? isDarkMode ? 'text-white' : 'text-[var(--p-hex)]' : isDarkMode ? 'text-gray-400' : 'text-gray-400'
                  }`}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Time Format */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.timeFormat')}
            </h2>
            <button
              onClick={toggleTimeFormat}
              data-testid="time-format-toggle"
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
              } ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-teal-500/20' : 'bg-teal-100'
                }`}>
                  <Clock className={`w-5 h-5 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`} />
                </div>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {use24Hour ? t('settings.use24Hour') : t('settings.use12Hour')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    {t('settings.timeFormatDesc')}
                  </p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full p-1 transition-colors ${use24Hour ? 'bg-teal-500' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${use24Hour ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            </button>
          </div>

          {/* Week Start Day */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.weekStartDay')}
            </h2>
            <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-rose-500/20' : 'bg-rose-100'
              }`}>
                <CalendarDays className={`w-5 h-5 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`} />
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('settings.weekStartDayDesc')}
              </p>
            </div>
            <Select
              value={weekStartDay.toString()}
              onValueChange={(val) => updateWeekStartDay(parseInt(val))}
            >
              <SelectTrigger className={`w-full ${
                isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'
              }`}>
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-[#1e293b] border-white/10' : ''}>
                {weekStartOptions.map((day) => (
                  <SelectItem
                    key={day.id}
                    value={day.id.toString()}
                    className={isDarkMode ? 'text-gray-200 focus:bg-white/10' : ''}
                  >
                    {t(day.key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Saved Verses */}
          <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <div className={`px-4 py-3 flex items-center gap-2 border-b ${isDarkMode ? 'border-white/8' : 'border-gray-100'} ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CrescentStarIcon size={14} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} />
              <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('verse.savedVerses')}
              </h2>
              {likedIds.length > 0 && (
                <span className="ml-auto px-2 py-0.5 text-xs font-bold rounded-full bg-rose-500 text-white">
                  {likedIds.length}
                </span>
              )}
            </div>
            <div className="p-4">
              {likedIds.length === 0 ? (
                <div className="flex items-center gap-3 py-2">
                  <Heart className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isAuthenticated ? t('verse.noSaved') : t('verse.loginToSave')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {likedIds.map(id => {
                    const verse = getVerseById(id);
                    if (!verse) return null;
                    const translation = verse.translations[language] || verse.translations.en;
                    return (
                      <div key={id} className={`rounded-xl p-3 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <p
                          className={`text-sm text-center leading-loose mb-1 ${isDarkMode ? 'text-amber-200' : 'text-[var(--p-hex)]'}`}
                          style={{ fontFamily: "'Amiri', serif", direction: 'rtl' }}
                        >
                          {verse.arabic}
                        </p>
                        {language !== 'ar' && (
                          <p className={`text-xs text-center italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            "{translation}"
                          </p>
                        )}
                        <div className={`flex items-center justify-between mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className={`text-[10px] ${isDarkMode ? 'text-amber-400/60' : 'text-[var(--p-hex)]/60'}`}>
                            {verse.reference}
                          </span>
                          <button
                            onClick={() => handleUnlike(id)}
                            className={`text-[10px] font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}
                          >
                            ✕ {t('qaza.missedLabel') === 'Missed' ? 'Remove' : '×'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Travel Mode: Qasr & Jama' */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.travelMode')}
            </h2>
            <button
              onClick={toggleTravelMode}
              data-testid="travel-mode-toggle"
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                travelMode
                  ? isDarkMode ? 'bg-sky-500/10 hover:bg-sky-500/20' : 'bg-sky-50 hover:bg-sky-100'
                  : isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
              } ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  travelMode ? isDarkMode ? 'bg-sky-500/20' : 'bg-sky-100' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                }`}>
                  <Plane className={`w-5 h-5 ${travelMode ? isDarkMode ? 'text-sky-400' : 'text-sky-600' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className={`font-medium ${travelMode ? isDarkMode ? 'text-sky-300' : 'text-sky-800' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('settings.qasr')} + {t('settings.jamee')}
                  </p>
                  <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('settings.travelModeDesc')}
                  </p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full p-1 transition-colors flex-shrink-0 ${travelMode ? 'bg-sky-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${travelMode ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            </button>
            {travelMode && (
              <p className={`mt-2 text-xs px-3 py-2 rounded-xl ${isDarkMode ? 'bg-sky-500/8 text-sky-300' : 'bg-sky-50 text-sky-700'}`}>
                {t('settings.travelModeNote')}
              </p>
            )}
          </div>

          {/* Contact Us */}
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.contactFeedback')}
            </h2>
            <a
              href="https://x.com/NoPrayApp"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
              } ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}>
                <svg viewBox="0 0 24 24" className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-800'}`} fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </div>
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>@NoPrayApp</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('settings.followOnX')}</p>
              </div>
              <ChevronRight className={`w-5 h-5 ml-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} ${isRTL ? 'rotate-180' : ''}`} />
            </a>
          </div>

          {/* About */}
          <div className={`rounded-2xl p-4 text-center ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              {t('settings.appTitle')}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-300'}`}>
              {t('settings.version')} 2.0 • {t('settings.madeWith')} ❤️ {t('settings.forUmmah')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
